import {
  clearTokens,
  getStoredTokens,
  isTokenExpired,
  storeTokens,
} from "./utils/tokenUtils";

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public data?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

interface FetchClientOptions extends RequestInit {
  baseURL?: string;
}

// ---------------------------------------------------------------------------
// Refresh-token lock
// A single shared Promise so that concurrent requests with an expired token
// all queue onto one refresh call instead of firing N parallel refresh calls.
// ---------------------------------------------------------------------------
let refreshPromise: Promise<string> | null = null;

export const performTokenRefresh = async (baseURL: string): Promise<string> => {
  // If a refresh is already in-flight, queue onto it
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const { refreshToken, username } = getStoredTokens();

    if (!refreshToken || !username) {
      throw new Error("No refresh token or username available");
    }

    // Strip any path suffix from baseURL to get the API origin/proxy root.
    // e.g. "/api-proxy/api/auth/v1" → "/api-proxy"
    // e.g. "https://example.com/api/auth/v1" → "https://example.com"
    const apiRoot = baseURL.replace(/\/api\/.*$/, "");

    // Scope the refreshed token to the currently selected tenant (when one is
    // set) so a refresh preserves the active tenant instead of falling back to
    // the primary. Matches how `selectTenant` sends tenantId (string).
    const tenantId = localStorage.getItem("tenantId");

    const response = await fetch(`${apiRoot}/api/auth/v1/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        refreshToken,
        username,
        ...(tenantId ? { tenantId } : {}),
      }),
    });

    if (!response.ok) {
      throw new Error(`Refresh failed with status ${response.status}`);
    }

    const data = (await response.json()) as {
      accessToken?: string;
      refreshToken?: string;
    };

    // A 2xx response without a usable access token must NOT be persisted:
    // storing `undefined` writes the literal string "undefined" to localStorage
    // and breaks every subsequent request. Treat it as a refresh failure.
    if (typeof data.accessToken !== "string" || data.accessToken.length === 0) {
      throw new Error("Refresh response did not contain an access token");
    }

    // Persist new tokens — refresh token rotation: store new RT if provided
    storeTokens({
      accessToken: data.accessToken,
      ...(data.refreshToken ? { refreshToken: data.refreshToken } : {}),
    });

    return data.accessToken;
  })().finally(() => {
    refreshPromise = null; // release lock regardless of outcome
  });

  return refreshPromise;
};

// ---------------------------------------------------------------------------
// Core fetch wrapper
// ---------------------------------------------------------------------------

/** Auth endpoints must never trigger a refresh attempt (would cause loops). */
const isAuthEndpoint = (url: string): boolean => url.includes("/api/auth/v1/");

/**
 * Called after a token refresh fails. If a *different*, still-valid access
 * token appeared in storage while the refresh was in flight — e.g. a tenant
 * switch replaced the tokens concurrently — return it so the caller can retry
 * with it. Otherwise clear tokens, signal logout, and return null.
 *
 * This prevents a stale, pre-switch request whose refresh fails from wiping the
 * freshly-issued tokens of the tenant the user just switched into.
 */
function recoverTokenOrClear(startToken: string | null): string | null {
  const { accessToken } = getStoredTokens();
  if (
    accessToken &&
    accessToken !== startToken &&
    !isTokenExpired(accessToken)
  ) {
    return accessToken;
  }
  clearTokens();
  window.dispatchEvent(new CustomEvent("auth:unauthorized"));
  return null;
}

export async function fetchClient<T>(
  url: string,
  options: FetchClientOptions = {},
  _isRetry = false,
): Promise<T> {
  const { baseURL, ...fetchOptions } = options;
  const fullUrl = url.startsWith("http") ? url : `${baseURL}${url}`;

  // Token this request intends to use, captured up-front. If it is replaced
  // concurrently (e.g. a tenant switch), the refresh-failure handlers below
  // recover with the new token instead of wiping the freshly-issued one.
  const startToken = getStoredTokens().accessToken;

  // -------------------------------------------------------------------------
  // Proactive expiry check — refresh before the request if token is expired
  // (or within the 30-second buffer window).  Skip for auth endpoints and
  // for retries (which already carry a fresh token in the Authorization header).
  // -------------------------------------------------------------------------
  if (!isAuthEndpoint(url) && !_isRetry) {
    if (startToken && isTokenExpired(startToken)) {
      try {
        const newToken = await performTokenRefresh(baseURL ?? "");
        fetchOptions.headers = {
          ...fetchOptions.headers,
          Authorization: `Bearer ${newToken}`,
        };
      } catch {
        // Proactive refresh failed. If a fresh token was stored concurrently
        // (e.g. a tenant switch), continue the request with it; otherwise clear
        // tokens, signal logout, and surface as a 401.
        const recovered = recoverTokenOrClear(startToken);
        if (!recovered) {
          throw new ApiError(401, "Session expired — please log in again");
        }
        fetchOptions.headers = {
          ...fetchOptions.headers,
          Authorization: `Bearer ${recovered}`,
        };
      }
    }
  }

  // -------------------------------------------------------------------------
  // Make the actual request
  // -------------------------------------------------------------------------
  const userLang =
    typeof navigator !== "undefined" ? navigator.language || "en-US" : "en-US";

  const headers: HeadersInit = {
    "Content-Type": "application/json",
    "Accept-Language": userLang,
    ...fetchOptions.headers,
  };

  const response = await fetch(fullUrl, { ...fetchOptions, headers });
  const data = await response.json().catch(() => null);

  if (!response.ok) {
    // -----------------------------------------------------------------------
    // Reactive 401 handler — catches clock-skew / edge cases where the token
    // appeared valid client-side but the server rejected it.
    // Only attempt once (_isRetry flag) and never on auth endpoints.
    // -----------------------------------------------------------------------
    if (response.status === 401 && !_isRetry && !isAuthEndpoint(url)) {
      try {
        const newToken = await performTokenRefresh(baseURL ?? "");
        return fetchClient<T>(
          url,
          {
            ...options,
            headers: {
              ...options.headers,
              Authorization: `Bearer ${newToken}`,
            },
          },
          true, // mark as retry — prevents infinite loop
        );
      } catch {
        // Refresh failed. If a fresh token arrived concurrently (tenant switch),
        // retry with it instead of wiping the new session; otherwise clear.
        const recovered = recoverTokenOrClear(startToken);
        if (recovered) {
          return fetchClient<T>(
            url,
            {
              ...options,
              headers: {
                ...options.headers,
                Authorization: `Bearer ${recovered}`,
              },
            },
            true, // mark as retry — prevents infinite loop
          );
        }
      }
    }

    throw new ApiError(
      response.status,
      data?.message || `HTTP ${response.status}`,
      data,
    );
  }

  return data as T;
}

// ---------------------------------------------------------------------------
// HTTP client factory
// ---------------------------------------------------------------------------
export const createHttpClient = (baseURL: string) => ({
  get: <T>(url: string, options?: FetchClientOptions) =>
    fetchClient<T>(url, { ...options, baseURL, method: "GET" }),

  post: <T>(url: string, body?: unknown, options?: FetchClientOptions) =>
    fetchClient<T>(url, {
      ...options,
      baseURL,
      method: "POST",
      body: body ? JSON.stringify(body) : undefined,
    }),

  put: <T>(url: string, body?: unknown, options?: FetchClientOptions) =>
    fetchClient<T>(url, {
      ...options,
      baseURL,
      method: "PUT",
      body: body ? JSON.stringify(body) : undefined,
    }),

  patch: <T>(url: string, body?: unknown, options?: FetchClientOptions) =>
    fetchClient<T>(url, {
      ...options,
      baseURL,
      method: "PATCH",
      body: body ? JSON.stringify(body) : undefined,
    }),

  delete: <T>(url: string, options?: FetchClientOptions) =>
    fetchClient<T>(url, { ...options, baseURL, method: "DELETE" }),
});
